
import { Component, OnInit } from '@angular/core';
import { ToastMessageOptions, SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DIALOG_ACTIONS_CONFIG } from '../dialog-actions-config';
import { UploadDialogConfig } from '../upload-dialog-config.interface';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MessageModule } from 'primeng/message';
import { FormsModule } from '@angular/forms';
import { ChooseNextAction } from '../choose-next-action.enum';

@Component({
    selector: 'app-pacim-upload-files-dialog',
    templateUrl: './pacim-upload-files-dialog.component.html',
    styleUrls: ['./pacim-upload-files-dialog.component.scss'],
    imports: [ButtonModule, FormsModule, MessageModule, RadioButtonModule]
})
export class PacimUploadFilesDialogComponent implements OnInit {
  nextAction: ChooseNextAction | undefined;

  actions: SelectItem<ChooseNextAction>[] = [];
  actionsMap!: Record<string, { disabled: boolean; label: ChooseNextAction; value: ChooseNextAction }>;

  hasLevelAction: boolean;
  showWarning = false;
  warningMessage = '';
  continuedStayMessage: ToastMessageOptions;
  private reconsiderationDays: number;

  constructor(
    private dialogRef: DynamicDialogRef,
    private dialogConfig: DynamicDialogConfig<UploadDialogConfig>
  ) {
    this.hasLevelAction = dialogConfig.data?.hasLevelAction;
    this.reconsiderationDays =
      dialogConfig.data?.programState === 'VA' ? 10 : 7;
  }

  ngOnInit(): void {
    this.initializeActionsTemplates();
    this.initializeActions();
  }

  onContinue(action: ChooseNextAction): void {
    this.dialogRef.close(action);
    this.dialogRef.destroy();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private initializeActionsTemplates(): void {
    this.actionsMap = this.hasLevelAction
      ? this.cloneDeep(DIALOG_ACTIONS_CONFIG)
      : this.omit(DIALOG_ACTIONS_CONFIG, ['level']);
  }

  private initializeActions(): void {
    const { serviceLines, isPacCommercial, message } = this.dialogConfig.data || {};

    this.continuedStayMessage = message;
    if (serviceLines) {
      this.setupActions(serviceLines, isPacCommercial);
    }

    this.actions = Object.values(this.actionsMap).map((action) => {
      return {
        label: action.label,
        value: action.value,
        disabled: action.disabled,
      };
    });
  }

  private setupActions(serviceLines: object[], isPacCommercial: boolean): void {
    if (this.continuedStayMessage?.severity === 'error') {
      this.actionsMap['continuedStay'].disabled = true;
    }

    if (serviceLines.some((line) => line['serviceStatus'] === 'Pending')) {
      if (this.hasLevelAction) {
        this.actionsMap['level'].disabled = true;
      }
    }
    else if (isPacCommercial && serviceLines.some(line => line['serviceStatus'] === 'Gold Card Provider')) {
      if (this.hasLevelAction) {
        this.actionsMap['level'].disabled = false;
      }
      this.actionsMap['continuedStay'].disabled = false;
      this.actionsMap['withoutRequest'].disabled = true;
    }
    else {
      const hasApprovedLine = serviceLines.some(line => line['serviceStatus'] === 'Approved');
      const latestDeniedLine = serviceLines
        .filter((line) => line['serviceStatus'] === 'Denied')
        .sort((line1, line2) =>
          this.sortByDate(
            line1['adjudicatedDate'] as string,
            line2['adjudicatedDate'] as string,
            'desc'
          )
        )[0];

      if (hasApprovedLine) {
        const nomncIssued = !isPacCommercial && this.dialogConfig.data?.documents?.some(
          (document) => document.documentType.includes('NOMNC')
        );

        if (!nomncIssued) {
          this.actionsMap['withoutRequest'].disabled = true;
        }
      } else if (latestDeniedLine) {
        this.actionsMap['continuedStay'].disabled = true;

        if (this.hasLevelAction) {
          this.actionsMap['level'].disabled = true;
        }

        const reconsiderationPassed = isPacCommercial && this.isReconsiderationPassed(
          latestDeniedLine['adjudicatedDate'] as string
        );

        if (reconsiderationPassed || !isPacCommercial) {
          this.showWarning = true;
          this.actionsMap['withoutRequest'].disabled = true;

          this.warningMessage = isPacCommercial
            ? `Reconsideration period ended. Please call a Health Plan representative for appeals.`
            : `Please call the number on the back of the member's ID card for appeals.`;
        }
      }
    }
  }

  private isReconsiderationPassed(adjudicatedDateString: string): boolean {
    const denialDate = new Date(adjudicatedDateString).getTime();
    const now = new Date().getTime();

    const daysPassed =
      new Date(
        denialDate + this.reconsiderationDays * 1000 * 3600 * 24
      ).getTime() <= now;

    return daysPassed;
  }

  private sortByDate(a: string, b: string, order: 'asc' | 'desc'): number {
    if (order === 'asc') {
      return new Date(a).getTime() - new Date(b).getTime();
    } else {
      return new Date(b).getTime() - new Date(a).getTime();
    }
  }

  // Created these methods instead of using lodash-es. If we decide we would rather use a 3rd party package, then we can remove this.
  private cloneDeep(obj: object) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(this.cloneDeep);
    }

    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, this.cloneDeep(value)])
    );
  }

  private omit(obj: object, keysToOmit: string[]) {
    const result = { ...obj };

    keysToOmit.forEach((key) => {
      if (key in result) {
        delete result[key];
      }
    });

    return result;
  }
}
